import { useState } from 'react';
import { ArrowLeft, UserPlus, X, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { useApprovers, useAvailableUsers, useAddApprover, useRemoveApprover } from '../hooks/useApprovers';
import { toast } from '@/shared/lib/toast';

export function IncidentSettingsPage() {
  const navigate = useNavigate();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [approverToRemove, setApproverToRemove] = useState<{ userId: string; name: string } | null>(null);

  const { data: approvers = [], isLoading: loadingApprovers } = useApprovers();
  const { data: availableUsers = [], isLoading: loadingAvailableUsers } = useAvailableUsers();
  const addApprover = useAddApprover();
  const removeApprover = useRemoveApprover();

  const handleAddApprover = async () => {
    if (!selectedUserId) {
      toast.error('Selecione um usuário');
      return;
    }

    try {
      await addApprover.mutateAsync(selectedUserId);
      toast.success('Aprovador adicionado com sucesso');
      setSelectedUserId('');
    } catch (error) {
      toast.error('Erro ao adicionar aprovador');
    }
  };

  const handleOpenRemoveDialog = (userId: string, name: string) => {
    setApproverToRemove({ userId, name });
    setConfirmDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!approverToRemove) return;

    try {
      await removeApprover.mutateAsync(approverToRemove.userId);
      toast.success('Aprovador removido com sucesso');
      setApproverToRemove(null);
    } catch (error) {
      toast.error('Erro ao remover aprovador');
    }
  };

  if (loadingApprovers || loadingAvailableUsers) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/incident-management')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6" />
              <h1 className="text-3xl font-bold">Configurações da Central de Problemas</h1>
            </div>
            <p className="text-muted-foreground">Gerencie aprovadores e configurações do módulo</p>
          </div>
        </div>
      </div>

      {/* Aprovadores Section */}
      <Card>
        <CardHeader>
          <CardTitle>Aprovadores de Advertências</CardTitle>
          <p className="text-sm text-muted-foreground">
            Usuários configurados como aprovadores podem aprovar ou rejeitar advertências pendentes.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Form para adicionar aprovador */}
          <div className="flex gap-4 p-4 border rounded-lg bg-muted/30">
            <div className="flex-1">
              <Select
                label="Adicionar Aprovador"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                options={[
                  { value: '', label: 'Selecione um usuário...' },
                  ...availableUsers.map((user) => ({
                    value: user.id,
                    label: `${user.name} (${user.email})`,
                  })),
                ]}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleAddApprover}
                disabled={!selectedUserId || addApprover.isPending}
              >
                {addApprover.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Adicionar
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Lista de aprovadores atuais */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Aprovadores Ativos ({approvers.length})</h3>
            {approvers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum aprovador configurado. Adicione um aprovador acima.
              </div>
            ) : (
              <div className="space-y-2">
                {approvers.map((approver) => (
                  <div
                    key={approver.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium">{approver.user.name}</p>
                        <p className="text-sm text-muted-foreground">{approver.user.email}</p>
                      </div>
                      <Badge variant="default">{approver.user.role}</Badge>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenRemoveDialog(approver.userId, approver.user.name)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Informações */}
      <Card>
        <CardHeader>
          <CardTitle>Informações</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm space-y-2">
            <p>
              <strong>Quem pode registrar incidentes:</strong> Todos os usuários com acesso à Central de Problemas podem registrar novos incidentes.
            </p>
            <p>
              <strong>Quem pode aprovar:</strong> Apenas os usuários configurados como aprovadores nesta página podem aprovar ou rejeitar advertências.
            </p>
            <p>
              <strong>Visualização:</strong> Os botões de aprovar/reprovar só aparecem para aprovadores configurados.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        title="Remover Aprovador"
        description={`Tem certeza que deseja remover ${approverToRemove?.name} da lista de aprovadores? Ele não poderá mais aprovar ou reprovar advertências.`}
        confirmText="Remover"
        cancelText="Cancelar"
        variant="destructive"
        icon="delete"
        onConfirm={handleConfirmRemove}
        isLoading={removeApprover.isPending}
      />
    </div>
  );
}
